/**
 * CandidatesReport
 * 
 * Standard candidates report. Uses shared candidate components.
 * Features: My/All toggle, table/card views, export, public view support.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../lib/api';
import { 
  UserCheck, 
  List, 
  LayoutGrid
} from 'lucide-react';
import { useWorkspaceReports } from '../../hooks/useWorkspaceReports';
import StatefulIcon from '../StatefulIcon';
import ReportLogoHeader from './ReportLogoHeader';

import {
  BaseCandidate,
  OrgCandidate,
  CandidateRenderContext,
  ViewMode,
  SortState,
  CandidateSortField,
  DEFAULT_SORT_STATE,
  DEFAULT_FILTER_STATE,
  useCandidateData,
  CandidateTable,
  CandidateCardGrid,
  CandidateTableColumn,
  CandidateCustomFieldColumn,
  applySortToData,
  collectCustomFieldColumns,
  normalizeCustomFieldValues,
} from '../candidates';
import { mapCandidateContactFields } from '../candidates/candidateContactUtils';
import ExportMenu from '../ExportMenu';

interface CandidatesReportProps {
  workspaceId?: string;
  workspaceName?: string;
  organizationId?: string;
  organizationName?: string;
  isPublicView?: boolean;
  prefetchedCandidates?: BaseCandidate[];
  /** When set (e.g. public report page), view mode is controlled by the parent */
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
}

type CandidateSource = 'my' | 'all';

/** All Candidates lists active candidates only (matches the workspace endpoint). */
const ALL_CANDIDATES_FILTERS = { ...DEFAULT_FILTER_STATE, status: 'active' };

const CandidatesReport: React.FC<CandidatesReportProps> = ({ 
  workspaceId,
  workspaceName: _workspaceName = 'Workspace',
  organizationId,
  organizationName: _organizationName,
  isPublicView = false,
  prefetchedCandidates,
  viewMode: viewModeProp,
  onViewModeChange,
}) => {
  const isOrgMode = !!organizationId;
  const hasPrefetched = Array.isArray(prefetchedCandidates);
  const [internalViewMode, setInternalViewMode] = useState<ViewMode>('card');
  const isControlledViewMode = viewModeProp !== undefined;
  const viewMode = isControlledViewMode ? viewModeProp : internalViewMode;

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    if (!isControlledViewMode) {
      setInternalViewMode(mode);
    }
    onViewModeChange?.(mode);
  }, [isControlledViewMode, onViewModeChange]);
  const [candidateSource, setCandidateSource] = useState<CandidateSource>('my');
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);
  const { exportReport } = useWorkspaceReports(workspaceId, organizationId);

  // Org-mode "My Candidates" — tracked across all org workspaces
  const { data: orgTrackedCandidates = [], isLoading: orgTrackedLoading } = useQuery(
    ['org-candidates', organizationId],
    async () => {
      const response = await api.get(`/org-reports/${organizationId}/candidates`);
      return response.data.data || [];
    },
    { enabled: isOrgMode && !hasPrefetched && candidateSource === 'my', staleTime: 30000 }
  );

  // Org-mode "All Candidates" — active candidates only, like the workspace
  // report's /candidates/all endpoint (the directory would include withdrawn).
  const { data: orgAllCandidates, isLoading: orgAllLoading } = useCandidateData<BaseCandidate>({
    source: 'all',
    filters: ALL_CANDIDATES_FILTERS,
    enabled: isOrgMode && !hasPrefetched && candidateSource === 'all',
  });

  // Workspace-mode fetch
  const { data: wsReportData, isLoading: wsLoading, error } = useCandidateData<BaseCandidate>({
    source: candidateSource === 'my' ? 'report' : 'report-all',
    workspaceId,
    enabled: !isOrgMode,
  });

  const reportData = useMemo(() => {
    if (!isOrgMode) return wsReportData || [];
    if (hasPrefetched) return prefetchedCandidates!;
    if (candidateSource === 'all') return orgAllCandidates || [];
    return orgTrackedCandidates as BaseCandidate[];
  }, [isOrgMode, hasPrefetched, prefetchedCandidates, candidateSource, orgTrackedCandidates, orgAllCandidates, wsReportData]);

  const isLoading = isOrgMode
    ? (hasPrefetched ? false : candidateSource === 'all' ? orgAllLoading : orgTrackedLoading)
    : wsLoading;

  // In org mode "My Candidates": normalize and aggregate workspace info per candidate.
  // Backend already deduplicates and attaches workspaces[] via orgReportBuilderService.
  // Skip deduplication for "All Candidates" — those come from the directory, no workspace data.
  const deduplicatedCandidates = useMemo((): OrgCandidate[] | null => {
    if (!isOrgMode || candidateSource === 'all' || !reportData || reportData.length === 0) return null;

    const firstRow = reportData[0] as unknown as Record<string, unknown>;

    // Backend pre-aggregated path: workspaces[] already built server-side
    if (Array.isArray(firstRow?.workspaces)) {
      return reportData.map((cand) => {
        const raw = cand as unknown as Record<string, unknown>;
        return {
          ...normalizeOrgCandidate(raw),
          workspaces: raw.workspaces as OrgCandidate['workspaces'],
        };
      });
    }

    // Legacy flat path: one row per candidate×workspace, deduplicate client-side
    const map = new Map<string | number, OrgCandidate>();
    for (const cand of reportData) {
      const raw = cand as unknown as Record<string, unknown>;
      const key = (raw.candidate_id as string | number | undefined) ?? cand.id;
      const wsEntry = {
        name: (raw.workspace_name as string) || '',
        id: (raw.workspace_id as string) || String(key),
        position: (raw.position as string) || undefined,
      };
      if (map.has(key)) {
        map.get(key)!.workspaces!.push(wsEntry);
      } else {
        map.set(key, { ...normalizeOrgCandidate(raw), workspaces: [wsEntry] });
      }
    }
    return Array.from(map.values());
  }, [isOrgMode, candidateSource, reportData]);

  const handleSort = useCallback((field: string) => {
    const sortKey = field as CandidateSortField;
    setSort((prev) => ({
      field: sortKey,
      direction: prev.field === sortKey && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const sortedData = useMemo(() => {
    if (!reportData) return [];
    const dataToSort = isOrgMode && deduplicatedCandidates ? deduplicatedCandidates : reportData;
    return applySortToData(dataToSort, sort);
  }, [reportData, deduplicatedCandidates, isOrgMode, sort]);

  // Only render columns for custom fields the org has actually populated —
  // an org with none sees the report exactly as before.
  const customFieldColumns: CandidateCustomFieldColumn[] = useMemo(
    () => collectCustomFieldColumns(sortedData),
    [sortedData]
  );

  const handleExport = async (format: string) => {
    try {
      await exportReport('candidates', format as 'csv' | 'excel' | 'pdf' | 'word', {
        viewMode,
        includeFullDetails: viewMode === 'card',
      } as any);
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  const renderContext: CandidateRenderContext = useMemo(() => ({
    workspaceId,
    isPublicView,
  }), [workspaceId, isPublicView]);

  // All Candidates is active-only, so its status column/chip would be constant.
  const showStatus = candidateSource === 'my';

  const tableColumns: CandidateTableColumn[] = showStatus
    ? ['name', 'office', 'district', 'party', 'status', 'incumbent', 'funds', 'contact', 'web']
    : ['name', 'office', 'district', 'party', 'incumbent', 'funds', 'contact', 'web'];

  const orgTableColumns: CandidateTableColumn[] = [
    'name', 'workspaces', 'office', 'district', 'party', 'status', 'incumbent', 'funds', 'contact', 'web',
  ];

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-text-tertiary dark:text-text-dark-tertiary">Loading candidates...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-error-500 dark:text-error-400">
            Error loading candidates: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6">
      <ReportLogoHeader workspaceId={workspaceId} />

      {/* The public share page already titles the report, names the
          organization and offers Export above this component; repeating them
          inside a second card was most of the nesting on a phone (Angie,
          2026-09-13). */}
      {!isPublicView && (
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-left text-text-primary dark:text-text-dark-primary flex items-center gap-2">
          <UserCheck className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          Candidates Report
        </h3>
        <p className="text-sm text-left text-text-secondary dark:text-text-dark-secondary mt-1">
          {candidateSource === 'my'
            ? (isOrgMode ? 'Tracked candidates across all workspaces' : 'Tracked candidates')
            : 'All active candidates'} with campaign finance and contact information
        </p>
      </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {!isPublicView && (
          <div className="flex items-center bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg p-1">
            <button
              onClick={() => setCandidateSource('my')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                candidateSource === 'my'
                  ? 'bg-white dark:bg-background-dark-secondary text-primary-700 dark:text-primary-300 shadow-sm'
                  : 'text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary'
              }`}
            >
              My Candidates
            </button>
            <button
              onClick={() => setCandidateSource('all')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                candidateSource === 'all'
                  ? 'bg-white dark:bg-background-dark-secondary text-primary-700 dark:text-primary-300 shadow-sm'
                  : 'text-text-secondary dark:text-text-dark-secondary hover:text-text-primary dark:hover:text-text-dark-primary'
              }`}
            >
              All Candidates
            </button>
          </div>
          )}

          <div className="flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-text-tertiary" />
            <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
              {sortedData.length} {candidateSource === 'my' ? 'tracked' : 'total'} candidates
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center space-x-2">
            <button
              onClick={() => handleViewModeChange('card')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'card'
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                  : 'bg-background-tertiary text-text-secondary hover:bg-neutral-200 dark:bg-background-dark-tertiary dark:text-text-dark-tertiary'
              }`}
              title="Card View"
            >
              <StatefulIcon icon={LayoutGrid} active={viewMode === 'card'} size="md" />
            </button>
            <button
              onClick={() => handleViewModeChange('table')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'table'
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                  : 'bg-background-tertiary text-text-secondary hover:bg-neutral-200 dark:bg-background-dark-tertiary dark:text-text-dark-tertiary'
              }`}
              title="Table View"
            >
              <StatefulIcon icon={List} active={viewMode === 'table'} size="md" />
            </button>
          </div>

          {!isPublicView && (
            <ExportMenu
              formats={viewMode === 'table' ? ['csv', 'excel', 'pdf'] : ['word', 'pdf']}
              onExport={(format) => handleExport(format)}
            />
          )}
        </div>
      </div>

      {sortedData.length === 0 ? (
        <div className="text-center py-12">
          <UserCheck className="w-12 h-12 text-text-tertiary dark:text-text-dark-tertiary mx-auto mb-4" />
          <h4 className="text-lg font-medium text-text-primary dark:text-text-dark-primary mb-2">
            {candidateSource === 'my' ? 'No Tracked Candidates' : 'No Candidates Found'}
          </h4>
          <p className="text-text-secondary dark:text-text-dark-secondary">
            {candidateSource === 'my'
              ? (isOrgMode
                  ? 'No candidates are being tracked in any workspace in this organization.'
                  : 'Start tracking candidates to see them in this report.')
              : 'No active candidates found.'}
          </p>
        </div>
      ) : (
        <>
          {/* Mobile: Card view only */}
          <div className="md:hidden">
            <CandidateCardGrid
              candidates={sortedData}
              context={renderContext}
              mode="workspace"
              gridCols="grid-cols-1"
              groupByOffice
              customFieldColumns={customFieldColumns}
              hideStatus={!showStatus}
            />
          </div>

          {/* Desktop: User choice */}
          <div className="hidden md:block">
            {viewMode === 'table' ? (
              <CandidateTable
                candidates={sortedData}
                isLoading={isLoading}
                columns={isOrgMode && candidateSource === 'my' ? orgTableColumns : tableColumns}
                context={renderContext}
                mode="workspace"
                variant="report"
                horizontalScroll={isOrgMode && candidateSource === 'my'}
                sort={sort}
                onSort={handleSort}
                customFieldColumns={customFieldColumns}
              />
            ) : (
              <CandidateCardGrid
                candidates={sortedData}
                context={renderContext}
                mode="workspace"
                gridCols="grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                groupByOffice
                customFieldColumns={customFieldColumns}
                hideStatus={!showStatus}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Normalize org-level candidate data (snake_case from backend) to BaseCandidate shape.
 */
function normalizeOrgCandidate(raw: Record<string, unknown>): BaseCandidate {
  const contact = mapCandidateContactFields({
    website_url: raw.website_url as string | null | undefined,
    website: raw.website as string | null | undefined,
    social_media: raw.social_media as Parameters<typeof mapCandidateContactFields>[0]['social_media'],
  });
  const id = raw.candidate_id ?? raw.id;
  const totalRaised = raw.total_raised ?? raw.totalFundsRaised;
  return {
    id: id != null ? String(id) : '',
    fullName: String(raw.full_name ?? raw.fullName ?? 'Unknown'),
    firstName: raw.first_name != null ? String(raw.first_name) : raw.firstName != null ? String(raw.firstName) : undefined,
    lastName: raw.last_name != null ? String(raw.last_name) : raw.lastName != null ? String(raw.lastName) : undefined,
    photoUrl: (raw.photo_url ?? raw.photoUrl ?? null) as string | null,
    officeSought: String(raw.office_sought ?? raw.officeSought ?? ''),
    district: raw.district != null ? String(raw.district) : null,
    partyAffiliation: raw.party_affiliation != null ? String(raw.party_affiliation) : raw.partyAffiliation != null ? String(raw.partyAffiliation) : null,
    electionYear: typeof raw.election_year === 'number'
      ? raw.election_year
      : typeof raw.electionYear === 'number'
        ? raw.electionYear
        : new Date().getFullYear(),
    status: String(raw.status ?? 'active'),
    isIncumbent: Boolean(raw.is_incumbent ?? raw.isIncumbent),
    incumbentLegislatorId: typeof raw.incumbent_legislator_id === 'number'
      ? raw.incumbent_legislator_id
      : typeof raw.incumbentLegislatorId === 'number'
        ? raw.incumbentLegislatorId
        : null,
    electionType: raw.election_type != null ? String(raw.election_type) : raw.electionType != null ? String(raw.electionType) : null,
    email: raw.email != null ? String(raw.email) : null,
    phone: raw.phone != null ? String(raw.phone) : null,
    website: contact.website,
    socialMedia: contact.socialMedia,
    activeStaffCount: typeof raw.active_staff_count === 'string'
      ? parseInt(raw.active_staff_count, 10) || 0
      : typeof raw.active_staff_count === 'number'
        ? raw.active_staff_count
        : typeof raw.activeStaffCount === 'number'
          ? raw.activeStaffCount
          : 0,
    totalFundsRaised: totalRaised != null
      ? (typeof totalRaised === 'string' ? parseFloat(totalRaised) || 0 : Number(totalRaised))
      : null,
    isPriority: Boolean(raw.is_priority ?? raw.isPriority),
    position: raw.position != null ? String(raw.position) as BaseCandidate['position'] : undefined,
    trackingNotes: raw.tracking_notes != null ? String(raw.tracking_notes) : raw.trackingNotes != null ? String(raw.trackingNotes) : undefined,
    seatIncumbentName: raw.seat_incumbent_name != null ? String(raw.seat_incumbent_name) : null,
    seatIncumbentId: (raw.seat_incumbent_id ?? null) as BaseCandidate['seatIncumbentId'],
    customFieldValues: normalizeCustomFieldValues(raw.custom_field_values),
  };
}

export default CandidatesReport;
